import { Injectable, signal } from '@angular/core';

/**
 * How long the app must stay idle before whenIdle() resolves. One flow often runs
 * its steps back to back (API call → wallet prompt → API call), leaving gaps of a
 * few milliseconds between them; a single idle tick is not a safe moment.
 */
export const IDLE_SETTLE_MS = 300;
/** How often whenIdle() re-checks the job counter. */
const IDLE_POLL_MS = 100;

@Injectable({ providedIn: 'root' })

export class SwProcessingService {
  private counter = 0;

  // Signal instead of BehaviorSubject
  private readonly _isProcessing = signal<boolean>(false);

  // Expose readonly signal (replacement for isProcessing$)
  readonly isProcessing = this._isProcessing.asReadonly();

  /** mark the start of a job */
  start() {
    this.counter++;

    if (this.counter > 0) {
      this._isProcessing.set(true);
    }
  }

  /** mark the end of a job */
  stop() {
    if (this.counter > 0) {
      this.counter--;
    }

    if (this.counter === 0) {
      this._isProcessing.set(false);
    }
  }

  /** force stop (useful for cleanup) */
  reset() {
    this.counter = 0;
    this._isProcessing.set(false);
  }

  /**
   * Resolves once no API call or contract transaction has been running for
   * `settleMs`. Used to hold back a page reload until nothing is in flight.
   *
   * @param settleMs - How long the app must stay idle, in milliseconds.
   * @returns A promise that resolves when the app has settled.
   */
  whenIdle(settleMs: number = IDLE_SETTLE_MS): Promise<void> {
    return new Promise<void>((resolve) => {
      let idleSince: number | null = null;
      const check = (): void => {
        if (this.counter > 0) {
          idleSince = null;
        } else if (idleSince === null) {
          idleSince = Date.now();
        }
        if (idleSince !== null && Date.now() - idleSince >= settleMs) {
          resolve();
          return;
        }
        setTimeout(check, IDLE_POLL_MS);
      };
      check();
    });
  }
}
